import { ApolloError } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';
import {
  CaraWorkspacePreset,
  CaraWorkspaceFileInput,
  type CaraWorkspaceEffortLevel,
  CustomPresetState,
  Visibility,
  type CustomPresetAccessFieldsFragment,
  type CustomPresetDetailFieldsFragment,
  type CustomPresetFieldsFragment,
  type FederatedChildAgencyFieldsFragment,
  type VisibilityConfigInput
} from '~/api/graphql';
import {
  type CaraAttachment,
  idsByKind,
  templateAttachment
} from '~/components/cara-input/cara-attachment';

/**
 * Use case identifiers for preset action types
 */
export type UseCase =
  | 'review_coi_eoi'
  | 'review_acords'
  | 'review_supplementals'
  | 'compare_quotes'
  | 'compare_renewal'
  | 'check_against_application'
  | 'compare_lease_to_policy'
  | 'review_vendor_contract'
  | 'review_coi_requirements'
  | 'ask_policy_question'
  | 'explore_exclusions'
  | 'summarize_coverage'
  | 'review_application'
  | 'verify_insured_info'
  | 'investigate_potential_misrep'
  | 'classify_naics'
  | 'classify_iso_gl'
  | 'classify_sic'
  | 'verify_classification'
  | 'bulk_class_review'
  | 'fill_acord_form'
  | 'fill_due_diligence'
  | 'fill_other_pdf'
  | 'generate_certificate'
  | 'generate_id_cards'
  | 'create_quote_proposal'
  | 'create_summary_document'
  | 'create_other_document'
  | 'sov_document_only'
  | 'sov_documents_and_web'
  | 'sov_address_only'
  | 'review_claim_viability'
  | 'review_claim_plan_next_steps'
  | 'check_eo_risk'
  | 'compile_loss_history'
  | 'research_claim_context'
  | 'negotiate_renewal_rate'
  | 'review_current_coverage'
  | 'review_claim_scenarios'
  | 'full_gap_analysis'
  | 'place_new_submission'
  | 'find_alternative_carriers'
  | 'research_alternative_classifications'
  | 'generate_risk_report'
  | 'review_industry_trends'
  | 'identify_risk_mitigations'
  | 'create_analysis'
  | 'build_contribution_worksheet'
  | 'solve_to_budget'
  | 'build_deduction_schedule'
  | 'review_benefits_guide'
  | 'verify_contributions'
  | 'format_for_platform'
  | 'clean_and_validate'
  | 'normalize_quotes'
  | 'build_marketing_grid'
  | 'build_finalist_comparison'
  | 'reconcile_first_invoice'
  | 'reconcile_monthly_invoice'
  | 'quarterly_review'
  | 'pre_renewal_review'
  | 'build_enrollment_form'
  | 'build_benefits_guide'
  | 'build_announcement'
  | 'convert_statement'
  | 'reconcile_statement'
  | 'analyze_commission';

/**
 * File state keys for different preset types
 */
export type PresetFileStateKeys =
  | 'certificate'
  | 'quote'
  | 'contract'
  | 'claim'
  | 'application'
  | 'option1'
  | 'option2'
  | 'input'
  | 'upload';

/**
 * Consolidated file state for preset creation/edit
 */
export type PresetFileState = Record<PresetFileStateKeys, CaraWorkspaceFileInput[]>;

/**
 * Create empty file state
 */
export const createEmptyPresetFileState = (): PresetFileState => ({
  certificate: [],
  quote: [],
  contract: [],
  claim: [],
  application: [],
  option1: [],
  option2: [],
  input: [],
  upload: []
});

/**
 * Configuration for file input fields for each preset type
 */
export const presetFileFieldConfigs: Record<
  CaraWorkspacePreset,
  Array<{ label: string; description: string; key: PresetFileStateKeys }>
> = {
  [CaraWorkspacePreset.CertificateReview]: [
    {
      label: 'Certificate of Insurance',
      description: 'Upload the certificate of insurance you want Cara to review.',
      key: 'certificate'
    },
    {
      label: 'Quotes or Policies',
      description:
        'Upload any quotes or policies you want Cara to compare to the certificate of insurance.',
      key: 'quote'
    }
  ],
  [CaraWorkspacePreset.ContractReview]: [
    {
      label: 'Contract',
      description: 'Upload the contract you want Cara to review for insurance requirements.',
      key: 'contract'
    },
    {
      label: 'Quotes or Policies',
      description:
        'Upload any quotes or policies you want Cara to compare to the contract requirements.',
      key: 'quote'
    }
  ],
  [CaraWorkspacePreset.CoverageComparison]: [
    {
      label: 'First Option',
      description:
        'Upload any quotes or policies you want Cara that comprise the first option to compare.',
      key: 'option1'
    },
    {
      label: 'Second Option',
      description: 'Upload any quotes or policies you want Cara to compare to the first option.',
      key: 'option2'
    }
  ],
  [CaraWorkspacePreset.GenerateQuoteProposal]: [
    {
      label: 'First Option',
      description:
        'Upload any quotes or binders you want Cara to use as part of the first option to present in the quote proposal.',
      key: 'option1'
    },
    {
      label: 'Second Option',
      description:
        'Upload any quotes or binders you want Cara to use as part of the second option to present in the quote proposal, if any.',
      key: 'option2'
    }
  ],
  [CaraWorkspacePreset.DocumentExploration]: [
    {
      label: 'Input Files',
      description: "Files you'd like Cara to use as context to answer questions",
      key: 'input'
    }
  ],
  [CaraWorkspacePreset.UnderwritingAnalysis]: [
    {
      label: 'Input Documents',
      description:
        'Upload any documents you want Cara to analyze for commercial underwriting purposes.',
      key: 'input'
    }
  ],
  [CaraWorkspacePreset.CommercialClassification]: [
    {
      label: 'Input Documents',
      description:
        'Upload any documents you want Cara to analyze for commercial classification purposes.',
      key: 'input'
    }
  ],
  [CaraWorkspacePreset.GenerateTemplate]: [
    {
      label: 'Input Files',
      description:
        "Any files you'd like Cara to parse and use as input to cross-file into templates.",
      key: 'input'
    }
  ],
  [CaraWorkspacePreset.SovAnalysis]: [
    {
      label: 'Input Documents',
      description: 'Upload any documents you want Cara to use for the Statement of Values.',
      key: 'input'
    }
  ],
  [CaraWorkspacePreset.ClaimsReview]: [
    {
      label: 'Claim Documents',
      description:
        'Upload claim-related documents such as the first notice of loss, claim forms, incident reports, damage assessments, or police reports.',
      key: 'claim'
    },
    {
      label: 'Policy Documents',
      description:
        'Upload the policy(ies) involved including declarations pages, policy forms, and endorsements.',
      key: 'quote'
    },
    {
      label: 'Application & Supporting Documents (Optional)',
      description:
        'Upload the original application and any supporting documents for misrepresentation analysis.',
      key: 'application'
    }
  ],
  [CaraWorkspacePreset.LossRunAnalysis]: [
    {
      label: 'Loss Run Documents',
      description: 'Upload the loss runs you want Cara to analyze.',
      key: 'input'
    },
    {
      label: 'Policy, Application, or Supporting Documents',
      description:
        'Upload the application or any supporting documents to provide additional context.',
      key: 'application'
    }
  ],
  [CaraWorkspacePreset.CoverageGapAnalysis]: [
    {
      label: 'Policy or Quote Documents (Recommended)',
      description: 'Include all lines of business in the submission.',
      key: 'input'
    },
    {
      label: 'Supporting Documents (Optional)',
      description:
        'Upload applications, schedules, or other documents that describe the client’s operations.',
      key: 'option1'
    }
  ],
  [CaraWorkspacePreset.RiskMatching]: [
    {
      label: 'Input Documents',
      description:
        'Upload application, financials, loss runs, or other documents for risk extraction.',
      key: 'input'
    }
  ],
  [CaraWorkspacePreset.RiskReport]: [
    {
      label: 'Business Documents (Optional)',
      description:
        'Upload any documents describing the business — applications, policies, or operational summaries. Cara will use these alongside deep industry research.',
      key: 'input'
    }
  ],
  [CaraWorkspacePreset.EbContributionAnalysis]: [
    {
      label: 'Census and Enrollment',
      description:
        'Eligibility roster with each employee’s plan, tier, class, and waiver status. Add salary or hourly rate and hours to test ACA affordability per employee. CSV or Excel.',
      key: 'input'
    },
    {
      label: 'Current Plan and Rates',
      description:
        'The expiring rate sheet and plan design (summary of benefits or SBC), plus what the employer contributes by tier. Without the split, structures are priced on their own rather than as a change from current.',
      key: 'quote'
    },
    {
      label: 'Renewal Plan and Rates (Optional)',
      description:
        'The incumbent’s renewal offer with plan design and rate guarantee. Separates the carrier’s rate action from your own enrollment change.',
      key: 'option1'
    },
    {
      // "Other" reads the three rate buckets as a sequence — what the group holds
      // now, what the incumbent offered, everything else. The optional tags stay
      // here and not on the preset form: a custom preset inheriting this one has no
      // use case, so it shows the union of the buckets and every one past the
      // census really can go unfilled.
      label: 'Other Plan Options (Optional)',
      description:
        'Competing carrier or plan quotes beyond the renewal; add as many as you want compared. Include each carrier’s benefit summary alongside its rates, and whatever a buy-down should be modeled from.',
      key: 'option2'
    },
    {
      label: 'Current Carrier Invoice (Optional)',
      description:
        'The most recent monthly bill. Reconciled against the census and current rates to verify the baseline before modeling.',
      key: 'application'
    }
  ],
  [CaraWorkspacePreset.EbDocumentReview]: [
    {
      label: 'Document Under Review',
      description:
        'The document being checked: the employee benefits guide, open enrollment packet, or contributions worksheet going out to employees.',
      key: 'input'
    },
    {
      label: 'Carrier Rate Sheets',
      description:
        'The rate exhibits the rates in the document should match, for the plan year under review. Add one per carrier.',
      key: 'quote'
    },
    {
      // A custom preset inheriting this one has no use case, so the tag stays:
      // it is honest for the worksheet run, and the description carries what
      // each missing source costs the guide run.
      label: 'Other Supporting Documents (Optional)',
      description:
        'Everything else the document was built from: the contributions worksheet with the employer and employee split, the plan documents (SBCs, benefit summaries, or the schedule of benefits), a revised carrier kit, a renewal letter, or a prior version. Without the worksheet, contributions are checked for arithmetic but not against the employer’s decision; without plan documents, every deductible, copay, and out-of-pocket maximum comes back unverified.',
      key: 'option1'
    }
  ],
  [CaraWorkspacePreset.EbCensusNormalization]: [
    {
      label: 'Census',
      description:
        'The roster to reformat: a payroll export, an enrollment platform report, or a PDF. Column names do not matter.',
      key: 'input'
    },
    {
      label: 'Destination Layout (Optional)',
      description:
        'A blank copy of the file you need filled, if it is not one of your saved layouts. Its headers are matched exactly and nothing is added to them.',
      key: 'option1'
    },
    {
      label: 'Field Requirements (Optional)',
      description:
        'The platform’s import guide or the carrier’s field list. Without it, the run can name the fields your census is missing but not which of them are required.',
      key: 'option2'
    },
    {
      label: 'Prior Census (Optional)',
      description:
        'Last renewal’s roster, for the adds, removals, and changes since. Compared only where both files cover the same group.',
      key: 'application'
    }
  ],
  // The dialog for this preset puts each option's documents in a card under a
  // name the broker types, so the pairing is stated rather than inferred. A
  // custom preset's file state is a fixed set of buckets with no repeatable
  // group in it, so this path is a flat list and the prompt falls back to
  // inferring the pairing from what each document says about itself — and to
  // publishing what it inferred.
  [CaraWorkspacePreset.EbQuoteAnalysis]: [
    {
      label: 'Comparison Census',
      description:
        'One census for the whole comparison, priced across every option, so a cheaper column is a cheaper price rather than a smaller group.',
      key: 'input'
    },
    {
      label: 'Carrier Quotes',
      description:
        'The rate exhibits and proposals that came back from marketing. Add one per carrier and per plan; age-banded, three-tier, and four-tier all work.',
      key: 'quote'
    },
    {
      label: 'Current Plan and Renewal (Optional)',
      description:
        'The expiring plan and the incumbent’s renewal offer, told apart by effective date. Every option is then measured against the offer the employer already has, not just against the others.',
      key: 'option1'
    },
    {
      label: 'Plan Documents (Optional)',
      description:
        'Benefit summaries, SBCs, or schedules of benefits for the quoted plans. Without them the comparison is cost only, and a cheaper plan that buys less reads as a saving.',
      key: 'option2'
    },
    {
      label: 'Current Contributions (Optional)',
      description:
        'What the employer contributes today, by tier. Only needed for the employer-versus-employee view; total cost comparison does not use it.',
      key: 'application'
    }
  ],
  [CaraWorkspacePreset.EbBillingAudit]: [
    {
      label: 'Carrier Invoice',
      description:
        'The bill being audited. A subscriber-level list bill supports the exception register; a summary bill carrying only tier counts is reconciled on counts alone.',
      key: 'input'
    },
    {
      label: 'Enrollment Roster',
      description:
        'Who is covered for the period billed, with plan, tier, and coverage dates. Prefer the employer’s or the benefits platform’s export: a roster pulled from the carrier’s portal only repeats the records the bill was made from.',
      key: 'quote'
    },
    {
      label: 'Bound Rates',
      description:
        'The sold-case confirmation or final rate sheet, at the rates the carrier agreed to rather than the ones it quoted. Without it the audit names who is billed wrongly but not what the charge should have been, so there is no variance and no credit amount.',
      key: 'option1'
    },
    {
      label: 'Submitted Changes and Carrier Confirmations (Optional)',
      description:
        'The adds, terminations, and changes sent to the carrier, with the dates they went and any confirmation or rejection. Submission dates are what separate a carrier billing error from a change the carrier was never given.',
      key: 'application'
    },
    {
      label: 'Contract and Billing Rules (Optional)',
      description:
        'The group contract, the carrier’s administrative or billing guide, or a policy exhibit: whichever carries the proration rule, the retroactive change limit, and the premium due date. Without one, nothing is tested against a term, because a market-typical threshold is an assumption rather than this group’s.',
      key: 'contract'
    },
    {
      label: 'Prior Invoice (Optional)',
      description:
        'Last period’s bill, for what changed since, which exceptions have persisted, and whether an adjustment was applied twice. Compared only where both bills cover the same group and account.',
      key: 'option2'
    }
  ],
  [CaraWorkspacePreset.EbClaimsReview]: [
    {
      label: 'Claims Experience',
      description:
        'The carrier’s or TPA’s experience report: paid claims by month, medical and pharmacy. Add monthly enrollment counts, pharmacy utilization, and funding or premium-equivalent rates here if they came separately. Only self-funded, level-funded, and large experience-rated groups have this data; a small fully insured group’s premium may not legally depend on its own claims.',
      key: 'input'
    },
    {
      label: 'Large Claimant Report',
      description:
        'The claimants above the carrier’s reporting threshold, with diagnosis category and paid-to-date. Most experience packages include one; if yours did not, ask the carrier or TPA for it. Without it there is no concentration figure and no stop-loss piercing check, the half of this review that finds money. Names are never reproduced in the output.',
      key: 'claim'
    },
    {
      label: 'Stop-Loss Contract (Optional)',
      description:
        'The specific deductible, the aggregate attachment, the contract basis, and any lasers. Without it no piercing is tested and none is guessed at: a reimbursement computed against a market-typical deductible is worse than no figure at all.',
      key: 'contract'
    }
  ],
  [CaraWorkspacePreset.EbEnrollmentMaterials]: [
    {
      label: 'This Year’s Rates, Benefit Summaries, and Contributions',
      description:
        'Everything the material is built from: the bound rates or sold-case confirmation, the benefit summaries or schedules of benefits for every line, and the contribution worksheet with the employer and employee split. Include the worksheet’s per-paycheck column if it has one, since that is where the deduction count comes from.',
      key: 'input'
    },
    {
      label: 'Last Year’s Material (Optional)',
      description:
        'The prior year’s guide, form, or announcement. It adds the what-changed section employees look for first, and it usually states how often employees are paid: a fallback when this year’s worksheet has no per-paycheck column and your notes do not say.',
      key: 'option1'
    }
  ],
  [CaraWorkspacePreset.CommissionProcessing]: [
    {
      label: 'Commission Statements',
      description:
        'The statements to process: PDF, Excel, or CSV, any number of carriers and periods in one run. Each is read in its own layout and footed to the total it prints before anything is reported from it.',
      key: 'input'
    },
    {
      label: 'Commission Schedule (Optional)',
      description:
        'The agreed rates by carrier, product, and group: a rate table, a carrier agreement, or the schedule exhibit. Without it each line is checked against the basis and rate the statement prints, not against what you agreed to be paid.',
      key: 'contract'
    },
    {
      label: 'Book of Business (Optional)',
      description:
        'A management system export of the accounts that should appear. An account the carrier did not pay on is not on the statement, so without an independent list its absence is invisible.',
      key: 'option1'
    },
    {
      label: 'Prior-Period Statements (Optional)',
      description:
        'Last period’s statements, for the period-over-period comparison. Without them there is no movement to decompose into rate, volume, accounts gained and lost, and adjustments.',
      key: 'option2'
    }
  ],
  // Deprecated presets - use GENERATE_TEMPLATE instead
  [CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance]: [],
  [CaraWorkspacePreset.GenerateEvidenceOfPropertyInsurance]: []
};

/**
 * Helper function to rename uploaded files with a prefix while preserving existing file references
 */
export const renameUploadedFiles = (
  files: CaraWorkspaceFileInput[],
  prefix: string
): CaraWorkspaceFileInput[] => {
  return files.map((fileInput) => {
    if ('upload' in fileInput && fileInput.upload) {
      const originalFile = fileInput.upload;
      const renamedFile = new File([originalFile], `${prefix}${originalFile.name}`, {
        type: originalFile.type,
        lastModified: originalFile.lastModified
      });
      return { upload: renamedFile };
    }
    // Pass through existing file references unchanged
    return fileInput;
  });
};

/**
 * Combines files from fileState based on the selected preset type
 */
export const combinePresetFiles = (
  fileState: PresetFileState,
  inheritFromPreset: CaraWorkspacePreset | null
): CaraWorkspaceFileInput[] => {
  if (inheritFromPreset) {
    const fieldConfigs = presetFileFieldConfigs[inheritFromPreset];
    if (fieldConfigs) {
      return fieldConfigs.flatMap((config) => fileState[config.key]);
    }
  }
  return fileState.upload;
};

/**
 * Creates FormData for custom preset mutations (create or update)
 * Extracts business logic for FormData creation from components
 */
export const createPresetFormData = (form: {
  title: string;
  description: string;
  basePrompt: string;
  allowFileUpload: boolean;
  inheritFromPreset?: CaraWorkspacePreset | null;
  defaultEffortLevel?: CaraWorkspaceEffortLevel | null;
  fileIds?: string[] | null;
  uploadFiles?: File[] | null;
  visibility?: VisibilityConfigInput | null;
  wordDocumentTemplateIds?: string[] | null;
  htmlDocumentTemplateIds?: string[] | null;
  customFormIds?: string[] | null;
}): FormData => {
  const formData = new FormData();
  formData.append('title', form.title);
  formData.append('description', form.description);
  formData.append('basePrompt', form.basePrompt);
  formData.append('allowFileUpload', form.allowFileUpload ? 'true' : 'false');
  if (form.inheritFromPreset) {
    formData.append('inheritFromPreset', form.inheritFromPreset);
  }
  if (form.defaultEffortLevel) {
    formData.append('defaultEffortLevel', form.defaultEffortLevel);
  }
  form.fileIds?.forEach((fileId) => {
    formData.append('fileIds', fileId);
  });
  form.uploadFiles?.forEach((file) => {
    formData.append('uploadFiles', file);
  });
  if (form.visibility) {
    formData.append('visibility', JSON.stringify(form.visibility));
  }
  form.wordDocumentTemplateIds?.forEach((id) => {
    formData.append('wordDocumentTemplateIds', id);
  });
  form.htmlDocumentTemplateIds?.forEach((id) => {
    formData.append('htmlDocumentTemplateIds', id);
  });
  form.customFormIds?.forEach((id) => {
    formData.append('customFormIds', id);
  });
  return formData;
};

type PresetTemplateRefSource =
  | Partial<
      Pick<
        CustomPresetDetailFieldsFragment,
        'wordDocumentTemplates' | 'htmlDocumentTemplates' | 'customForms'
      >
    >
  | null
  | undefined;

export const presetTemplateAttachments = (preset: PresetTemplateRefSource): CaraAttachment[] => [
  ...(preset?.wordDocumentTemplates ?? []).map((t) => templateAttachment('word-template', t)),
  ...(preset?.htmlDocumentTemplates ?? []).map((t) => templateAttachment('html-template', t)),
  ...(preset?.customForms ?? []).map((f) => templateAttachment('custom-form', f))
];

export type PresetTemplateRefs = {
  wordDocumentTemplateIds: string[] | null;
  htmlDocumentTemplateIds: string[] | null;
  customFormIds: string[] | null;
};

// With the toggle off every list is null regardless of what is queued: the queue
// is retained only so toggling back on restores the prior selection.
export const presetTemplateRefsForSubmit = (
  useOutputTemplates: boolean,
  attachments: CaraAttachment[]
): PresetTemplateRefs => {
  if (!useOutputTemplates) {
    return { wordDocumentTemplateIds: null, htmlDocumentTemplateIds: null, customFormIds: null };
  }
  const wordDocumentTemplateIds = idsByKind(attachments, 'word-template');
  const htmlDocumentTemplateIds = idsByKind(attachments, 'html-template');
  const customFormIds = idsByKind(attachments, 'custom-form');
  return {
    wordDocumentTemplateIds: wordDocumentTemplateIds.length > 0 ? wordDocumentTemplateIds : null,
    htmlDocumentTemplateIds: htmlDocumentTemplateIds.length > 0 ? htmlDocumentTemplateIds : null,
    customFormIds: customFormIds.length > 0 ? customFormIds : null
  };
};

export type WorkspaceTemplateCreateOptions = {
  customFormIds?: string[];
  wordDocumentTemplateIds?: string[];
  htmlDocumentTemplateIds?: string[];
  excelSpreadsheetTemplateIds?: string[];
};

/** Maps queued output-template attachments to createCaraWorkspace template id fields. */
export const templateWorkspaceOptionsFromAttachments = (
  useOutputTemplates: boolean,
  attachments: CaraAttachment[]
): WorkspaceTemplateCreateOptions => {
  if (!useOutputTemplates) {
    return {};
  }

  const customFormIds = idsByKind(attachments, 'custom-form');
  const wordDocumentTemplateIds = idsByKind(attachments, 'word-template');
  const htmlDocumentTemplateIds = idsByKind(attachments, 'html-template');
  const excelSpreadsheetTemplateIds = idsByKind(attachments, 'excel-template');

  return {
    ...(customFormIds.length ? { customFormIds } : {}),
    ...(wordDocumentTemplateIds.length ? { wordDocumentTemplateIds } : {}),
    ...(htmlDocumentTemplateIds.length ? { htmlDocumentTemplateIds } : {}),
    ...(excelSpreadsheetTemplateIds.length ? { excelSpreadsheetTemplateIds } : {})
  };
};

/**
 * Shared presets are granted read/run-only (VIEWER) access, so every owner-only
 * control gates on `isOwner`, which is false for a preset shared in from a parent.
 */

export const canEditPreset = (preset: Pick<CustomPresetFieldsFragment, 'isOwner'>): boolean =>
  preset.isOwner;

export const canSelectPreset = (preset: Pick<CustomPresetFieldsFragment, 'isOwner'>): boolean =>
  preset.isOwner;

/**
 * Mirrors CustomPreset.IsReady on the server. A GENERATING preset has no base
 * prompt yet and one that FAILED never will, so both createCaraWorkspace and
 * updateCustomPreset reject them; every control that would reach either must
 * gate on this or it is a dead end. NEEDS_REVIEW is finished and passes.
 */
export const isPresetReady = (preset: Pick<CustomPresetFieldsFragment, 'state'>): boolean =>
  preset.state === CustomPresetState.Ready || preset.state === CustomPresetState.NeedsReview;

/**
 * Shared presets go straight to the run flow so they never reach the edit page.
 * Undefined for a preset that is not ready, which leaves the row inert.
 */
export const presetRowHref = (
  preset: Pick<CustomPresetFieldsFragment, 'id' | 'isOwner' | 'state'>
): string | undefined => {
  if (!isPresetReady(preset)) {
    return undefined;
  }
  return canEditPreset(preset) ? `/app/presets/${preset.id}/edit` : `/app/presets/${preset.id}/run`;
};

/**
 * Badge label for a row shared in from another agency; null for an owned row.
 * "Network" rather than "Shared" because the app already spends that word on
 * Visibility.SHARED — "specific people and teams inside this agency".
 */
export const sharedAccessBadgeLabel = (
  preset: Pick<CustomPresetFieldsFragment, 'isOwner' | 'myAccess'>
): string | null => (!preset.isOwner && preset.myAccess ? 'Network' : null);

export const selectablePresetIds = (
  presets: Array<Pick<CustomPresetFieldsFragment, 'id' | 'isOwner'>>
): string[] => presets.filter(canSelectPreset).map((preset) => preset.id);

/**
 * Restricts selected IDs to presets the caller owns. Defense in depth so a shared,
 * read-only preset can never enter a bulk archive / visibility action.
 */
export const filterSelectablePresetIds = (
  presets: Array<Pick<CustomPresetFieldsFragment, 'id' | 'isOwner'>>,
  selectedIds: string[]
): string[] => {
  const selectable = new Set(selectablePresetIds(presets));
  return selectedIds.filter((id) => selectable.has(id));
};

/**
 * Whether the Share affordance is shown. Mirrors what the backend enforces: owner
 * only, admin only (CUSTOM_PRESET:SHARE), and PUBLIC only — PRIVATE and SHARED
 * limit visibility within the owning agency. The empty child list keeps the action
 * off standalone agencies, which have nowhere to share to.
 */
export const canSharePreset = (
  preset: Pick<CustomPresetFieldsFragment, 'isOwner' | 'visibility'>,
  hasSharePermission: boolean,
  federatedChildAgencies: FederatedChildAgencyFieldsFragment[]
): boolean =>
  preset.isOwner &&
  hasSharePermission &&
  preset.visibility.level === Visibility.Public &&
  federatedChildAgencies.length > 0;

/**
 * The grants to add and remove to make the shared set match what is checked in the
 * share dialog. A checkbox represents shared state, so one Update reconciles both
 * directions.
 */
export const shareSelectionDiff = (
  grantedAgencyIds: string[],
  selectedAgencyIds: string[]
): { toGrant: string[]; toRevoke: string[] } => {
  const granted = new Set(grantedAgencyIds);
  const selected = new Set(selectedAgencyIds);
  return {
    toGrant: selectedAgencyIds.filter((id) => !granted.has(id)),
    toRevoke: grantedAgencyIds.filter((id) => !selected.has(id))
  };
};

/** A child agency row in the share table, carrying its grant when one exists. */
export interface ShareAgencyRow<T extends { id: string; name: string }> {
  agency: T;
  access: CustomPresetAccessFieldsFragment | null;
}

/**
 * The share table's rows: shared agencies first (most recent grant first), then the
 * rest in their original order. Ordering keys off the grant, never the selection, so
 * rows hold still while boxes are being ticked and only reorder once a share lands —
 * putting the agencies just shared with at the top.
 */
export const shareAgencyRows = <T extends { id: string; name: string }>(
  childAgencies: T[] | null,
  accessList: CustomPresetAccessFieldsFragment[]
): ShareAgencyRow<T>[] => {
  if (!childAgencies) {
    return [];
  }
  const accessByAgencyId = new Map(accessList.map((access) => [access.agency.id, access]));
  const rows = childAgencies.map((agency) => ({
    agency,
    access: accessByAgencyId.get(agency.id) ?? null
  }));
  const granted = rows.filter((row) => row.access);
  const ungranted = rows.filter((row) => !row.access);
  granted.sort(
    (a, b) => new Date(b.access!.createdAt).getTime() - new Date(a.access!.createdAt).getTime()
  );
  return [...granted, ...ungranted];
};

/**
 * Inline error for a share query or mutation result. Application and validation
 * messages are written for users and are surfaced; anything else falls back to the
 * caller's message. Never relay the raw GraphQL error — that channel carries the
 * authorize directive's internals and DB failures wrapped as "error executing <SQL>
 * [args: ...]", which the server's presenter does not strip.
 */
export const shareErrorMessage = (
  errors: ReadonlyArray<GraphQLFormattedError> | undefined,
  fallback: string
): string | undefined => {
  if (!errors?.length) {
    return undefined;
  }
  const error = new ApolloError({ graphQLErrors: errors });
  return (
    getApplicationError(error)?.message ?? getValidationErrors(error)?.[0]?.message ?? fallback
  );
};

/**
 * Inline message for a multi-agency update, or undefined when every grant and revoke
 * landed. Names each agency that did not change: after a fan-out a bare failure
 * leaves the user unable to tell which operations landed.
 */
export const shareUpdateFailureMessage = (
  failures: Array<{ name: string; message: string }>
): string | undefined =>
  failures.map((failure) => `${failure.name}: ${failure.message}`).join(' ') || undefined;

/**
 * A generated preset the author has not accepted yet. Fully usable; the first
 * manual save promotes it to READY server-side.
 */
export const presetNeedsReview = (preset: Pick<CustomPresetFieldsFragment, 'state'>): boolean =>
  preset.state === CustomPresetState.NeedsReview;
